/**
 * ActivityTagSelector — Curated chip-based tag selector for activities.
 * Collapsible groups, collapsed by default. Same visual model as PlaceCore.
 * Max 5 tags. No free-text input.
 * SoT: docs/architecture/activity-tags-v1.md
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useActivityTagOptions } from '@/hooks/useActivityTagOptions';
import { PLACE_TAG_GROUPS, ALL_GROUPED_SLUGS, EXCLUDED_TAG_SLUGS } from '@/lib/tags/placeTagGroups';
import { cn } from '@/lib/utils';

const MAX_ACTIVITY_TAGS = 5;

interface ActivityTagSelectorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

export function ActivityTagSelector({ value, onChange, disabled }: ActivityTagSelectorProps) {
  const { options, loading } = useActivityTagOptions();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleTag = (slug: string) => {
    if (disabled) return;
    if (value.includes(slug)) {
      onChange(value.filter((t) => t !== slug));
    } else {
      if (value.length >= MAX_ACTIVITY_TAGS) return;
      onChange([...value, slug]);
    }
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const atLimit = value.length >= MAX_ACTIVITY_TAGS;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Laddar taggar…
      </div>
    );
  }

  const renderChips = (slugs: { slug: string; label: string }[]) => (
    <div className="flex flex-wrap gap-1.5 pt-2 pb-1">
      {slugs.map((opt) => {
        const selected = value.includes(opt.slug);
        const isDisabled = disabled || (!selected && atLimit);
        return (
          <Badge
            key={opt.slug}
            role="button"
            tabIndex={0}
            variant={selected ? 'default' : 'outline'}
            className={cn(
              'select-none transition-colors',
              isDisabled && !selected
                ? 'opacity-40 cursor-not-allowed'
                : selected
                ? 'bg-accent text-accent-foreground cursor-pointer'
                : 'cursor-pointer hover:bg-muted'
            )}
            onClick={() => !isDisabled && toggleTag(opt.slug)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!isDisabled) toggleTag(opt.slug);
              }
            }}
          >
            {opt.label}
          </Badge>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground mb-2">
        Välj upp till {MAX_ACTIVITY_TAGS} taggar
        {value.length > 0 && (
          <span className="ml-1">({value.length}/{MAX_ACTIVITY_TAGS})</span>
        )}
      </p>

      {PLACE_TAG_GROUPS.map((group) => {
        const groupOptions = options.filter(
          (o) => group.slugs.includes(o.slug) && !EXCLUDED_TAG_SLUGS.has(o.slug)
        );
        if (groupOptions.length === 0) return null;

        const selectedInGroup = groupOptions.filter((o) => value.includes(o.slug)).length;
        const isExpanded = expandedGroups.has(group.key);

        return (
          <div key={group.key} className="border rounded-lg">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors rounded-lg"
              onClick={() => toggleGroup(group.key)}
              aria-expanded={isExpanded}
            >
              <span className="text-sm">
                {group.icon} {group.title}
              </span>
              {selectedInGroup > 0 && (
                <span className="text-xs text-primary font-medium">({selectedInGroup})</span>
              )}
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 ml-auto text-muted-foreground transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            </button>
            {isExpanded && (
              <div className="px-3 pb-2.5">
                {renderChips(groupOptions)}
              </div>
            )}
          </div>
        );
      })}

      {/* Ungrouped tags */}
      {(() => {
        const ungrouped = options.filter(
          (o) => !ALL_GROUPED_SLUGS.has(o.slug) && !EXCLUDED_TAG_SLUGS.has(o.slug)
        );
        if (ungrouped.length === 0) return null;

        const selectedInGroup = ungrouped.filter((o) => value.includes(o.slug)).length;
        const isExpanded = expandedGroups.has('_other');

        return (
          <div className="border rounded-lg">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors rounded-lg"
              onClick={() => toggleGroup('_other')}
              aria-expanded={isExpanded}
            >
              <span className="text-sm">Övrigt</span>
              {selectedInGroup > 0 && (
                <span className="text-xs text-primary font-medium">({selectedInGroup})</span>
              )}
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 ml-auto text-muted-foreground transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            </button>
            {isExpanded && (
              <div className="px-3 pb-2.5">
                {renderChips(ungrouped)}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
